<template>
  <div class="event-card" :class="{ 'event-card--list': displayMode === 'list' }">
    <div class="event-image-wrapper">
      <img :src="workshop.pictureUrl" :alt="workshop.name" />
      <div v-if="isSeries" class="event-date-badge event-date-badge--series">
        <span class="date-range" style="display:block">{{ seriesRangeLabel }}</span>
        <span class="date-sessions" style="display:block">{{ $tc('series.sessionsAvailable', seriesAvailableSessionsCount, { count: seriesAvailableSessionsCount }) }}</span>
      </div>
      <div v-else class="event-date-badge">
        <span class="date-day">{{ formatDay(workshop.datetime) }}</span>
        <span class="date-month">{{ formatMonth(workshop.datetime) }}</span>
        <span class="date-time" v-if="!workshop.isEntireDay">{{ formatTime(workshop.datetime) }}</span>
      </div>
      <div class="event-place-badge" v-if="workshop.place">
        <span class="place-name">{{ displayPlaceName }}</span>
      </div>
    </div>

    <div class="event-content">
      <h3 class="event-title">{{ workshop.name }}</h3>
      <div class="event-location" v-if="placeLocation">
        {{ placeLocation }}
      </div>
      <div class="event-price" :class="{ 'event-price--free': !hasPrice }">
        <template v-if="hasPrice">
          {{ formattedPrice }}
        </template>
        <template v-else>
          {{ $t('common.free') }}
        </template>
        <span v-if="workshop.requiresOnlinePayment" class="event-badge event-badge--online-payment">
          Paiement en ligne
        </span>
      </div>
      <div
        class="event-description"
        :class="{ 'expanded': showFullDescription }"
        v-html="displayedDescription"
      ></div>
      <span
        class="event-see-more"
        v-if="hasMoreContent"
        @click="toggleDescription"
      >
        {{ showFullDescription ? 'Voir moins' : $t('common.seeMore') }}
      </span>
    </div>

    <div class="event-footer">
      <button
        class="btn btn-reserve"
        @click="handleReserve"
        :disabled="isLoading || isFull"
      >
        <template v-if="isLoading">
          <b-spinner small></b-spinner>
        </template>
        <template v-else-if="isFull">
          COMPLET
        </template>
        <template v-else>
          {{ $t('events.reserve') }}
        </template>
      </button>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'

export default {
  name: 'EventCard',

  props: {
    workshop: {
      type: Object,
      required: true
    },
    displayMode: {
      type: String,
      default: 'grid'
    }
  },

  data() {
    return {
      showFullDescription: false,
      isLoading: false
    }
  },

  computed: {
    isSeries() {
      return this.workshop.kind === 'series'
    },

    seriesAvailableSessionsCount() {
      if (!this.isSeries || !this.workshop.recurrence) return 0
      return this.workshop.recurrence.availableSessionsCount || 0
    },

    seriesRangeLabel() {
      if (!this.isSeries || !this.workshop.recurrence) return ''
      const start = this.$dayjs(this.workshop.recurrence.startDate).format('DD/MM')
      const end = this.$dayjs(this.workshop.recurrence.endDate).format('DD/MM')
      return this.$t('series.dateRange', { start: start, end: end })
    },

    isFull() {
      if (this.isSeries) {
        return this.seriesAvailableSessionsCount === 0
      }
      // availableSeats inclut les accompagnants, participantsCount ne compte que les inscrits
      return this.workshop.availableSeats <= 0
    },

    displayPlaceName() {
      if (!this.workshop.place) return ''
      if (this.workshop.forcedPlaceName) {
        return `${this.workshop.place.label} (${this.workshop.forcedPlaceName})`
      }
      return this.workshop.place.label
    },

    placeLocation() {
      if (!this.workshop.place) return null
      const parts = [
        this.workshop.place.address,
        this.workshop.place.city
      ].filter(Boolean)
      return parts.length > 0 ? parts.join(', ') : null
    },

    hasPrice() {
      return this.workshop.price && this.workshop.price > 0
    },

    formattedPrice() {
      if (!this.hasPrice) return null
      // Price is stored in cents, convert to euros
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(this.workshop.price / 100)
    },

    sanitizedDescription() {
      if (!this.workshop.description) return ''
      return DOMPurify.sanitize(this.workshop.description, {
        ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'p', 'br', 'ul', 'ol', 'li', 'a', 'img', 'hr'],
        ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'class', 'data-read-more']
      })
    },

    readMoreMarkerMatch() {
      // Retourne le match du marqueur ou null si non trouvé
      return this.sanitizedDescription.match(/<hr[^>]*class="read-more-separator"[^>]*>/i)
    },

    shortDescription() {
      if (!this.sanitizedDescription) return ''

      // Priorité 1: Couper au marqueur "court/long" si présent
      const match = this.readMoreMarkerMatch
      if (match) {
        const separatorIndex = this.sanitizedDescription.indexOf(match[0])
        return this.sanitizedDescription.substring(0, separatorIndex)
      }

      // Priorité 2: Fallback à 255 caractères
      const text = this.sanitizedDescription.replace(/<[^>]*>/g, '')
      if (text.length <= 255) return this.sanitizedDescription

      let cutPoint = 255
      while (cutPoint > 200 && text[cutPoint] !== ' ') cutPoint--

      return this.truncateHtml(this.sanitizedDescription, cutPoint) + '...'
    },

    hasMoreContent() {
      // Si marqueur présent, il y a toujours du contenu "long"
      if (this.readMoreMarkerMatch) return true
      // Sinon, vérifier si > 255 caractères
      const text = this.sanitizedDescription.replace(/<[^>]*>/g, '')
      return text.length > 255
    },

    displayedDescription() {
      if (this.showFullDescription) {
        // Retirer le séparateur du contenu complet (avec flag g pour remplacer toutes les occurrences)
        return this.sanitizedDescription.replace(/<hr[^>]*class="read-more-separator"[^>]*>/gi, '')
      }
      return this.shortDescription
    }
  },

  methods: {
    formatDay(datetime) {
      return this.$dayjs(datetime).format('DD')
    },

    formatMonth(datetime) {
      return this.$dayjs(datetime).format('MMM YYYY').toUpperCase()
    },

    formatTime(datetime) {
      const start = this.$dayjs(datetime)
      const end = start.add(this.workshop.duration, 'minute')
      return `${start.format('HH:mm')} à ${end.format('HH:mm')}`
    },

    toggleDescription() {
      this.showFullDescription = !this.showFullDescription
    },

    handleReserve() {
      this.$emit('reserve', this.workshop)
    },

    truncateHtml(html, maxLength) {
      let textLength = 0
      let result = ''
      let inTag = false
      let tagBuffer = ''
      const openTags = []

      for (let i = 0; i < html.length && textLength < maxLength; i++) {
        const char = html[i]

        if (char === '<') {
          inTag = true
          tagBuffer = '<'
        } else if (char === '>') {
          inTag = false
          tagBuffer += '>'
          result += tagBuffer

          const tagMatch = tagBuffer.match(/<\/?(\w+)/)
          if (tagMatch) {
            if (tagBuffer[1] !== '/') {
              openTags.push(tagMatch[1])
            } else {
              openTags.pop()
            }
          }
          tagBuffer = ''
        } else if (inTag) {
          tagBuffer += char
        } else {
          result += char
          textLength++
        }
      }

      while (openTags.length > 0) {
        result += `</${openTags.pop()}>`
      }

      return result
    }
  }
}
</script>

<style lang="scss" scoped>
.event-description {
  &.expanded {
    -webkit-line-clamp: unset;
    overflow: visible;
  }

  // Support HTML content
  :deep(p) { margin: 0.5rem 0; }
  :deep(a) { color: #0066cc; text-decoration: underline; }
  :deep(img) { max-width: 100%; height: auto; }
  :deep(ul), :deep(ol) { padding-left: 1.5rem; }

  // Masquer le séparateur dans le rendu final
  :deep(hr.read-more-separator) { display: none; }
}

.event-price {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;

  &--free {
    color: #28a745;
  }
}

.event-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &--online-payment {
    background-color: #e8f4fd;
    color: #0066cc;
    border: 1px solid #b3d7f5;
  }
}
</style>
